// MailStatusCard — shows the outgoing e-mail transport (Gmail SMTP / Resend) and sends a test e-mail.
import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, Loader2, Send } from 'lucide-react';
import { apiCall } from './_shared';

export const MailStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => apiCall('/api/admin/mail/status').then(setStatus).catch(() => setStatus({ configured: false }));
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiCall('/api/admin/mail/test', { method: 'POST', body: JSON.stringify({ to: to.trim() || null }) });
      setMsg(`Test e-mail sent to ${r.to} via ${r.transport}.`);
      load();
    } catch (e) { setMsg(`Failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const label = status.transport === 'gmail' ? 'Gmail SMTP' : status.transport === 'resend' ? 'Resend' : 'not configured';
  return (
    <div className={`border rounded-xl p-3 flex flex-wrap items-center gap-3 text-sm ${status.configured ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="mail-status-card">
      {status.configured ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold text-slate-900" data-testid="mail-status-transport">Automatic e-mail: {label}</div>
        {status.configured
          ? <div className="text-xs text-slate-600">All system e-mails are sent from <b data-testid="mail-status-sender">{status.sender}</b>.</div>
          : <div className="text-xs text-amber-800">Add GMAIL_USER + GMAIL_APP_PASSWORD (or RESEND_API_KEY) to activate delivery.</div>}
        {status.last_test && <div className="text-xs text-slate-500">Last test: {status.last_test.ok ? 'sent' : 'failed'} to {status.last_test.to} · {String(status.last_test.at).slice(0, 16).replace('T', ' ')}</div>}
      </div>
      {status.configured && (
        <div className="flex items-center gap-2">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder={status.sender} className="border rounded px-2 py-1 text-xs w-52" data-testid="mail-test-to" />
          <button type="button" onClick={sendTest} disabled={busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-3 py-1 text-xs font-semibold disabled:opacity-60" data-testid="mail-test-send">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send test
          </button>
        </div>
      )}
      {msg && <div className="w-full text-xs text-slate-800" data-testid="mail-test-result">{msg}</div>}
    </div>
  );
};

export default MailStatusCard;
